import { useParams, Link } from "react-router-dom";
import { MapPin, Check, ArrowRight } from "lucide-react";
import SEO, { breadcrumbSchema, faqSchema, serviceSchema } from "../components/SEO";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import BookingWidget from "../components/BookingWidget";
import FaqAccordion from "../components/FaqAccordion";
import Reveal from "../components/Reveal";
import { CITIES } from "../data/content";
import { CITIES_HI, WHY_HI } from "../data/hindi";
import { useLang } from "../i18n";
import NotFound from "./NotFound";

const cap = (s) => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const WHY_EN = [
  "Clean, air-conditioned vehicles",
  "Professional local drivers",
  "Transparent fare quoted before booking",
  "On-time pickup, day or night",
  "One-way, round-trip & multi-day options",
  "24/7 phone & WhatsApp assistance",
];

export default function CityPage({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const city = CITIES.find((c) => c.slug === slug);
  const { lang, t } = useLang();
  if (!city) return <NotFound />;

  const hi = lang === "hi" ? CITIES_HI[slug] : null;
  const intro = hi?.intro || city.intro;
  const blocks = [
    [t("cLocal"), hi?.local || city.local],
    [t("cAirport"), hi?.airport || city.airport],
    [t("cOut"), hi?.outstation || city.outstation],
    [t("cSight"), t("cSightText").replace("{city}", city.name)],
  ];
  const faqs = hi?.faqs || city.faqs;
  const why = lang === "hi" ? WHY_HI : WHY_EN;
  const title = `Taxi Service in ${city.name}`;
  const tc = (k) => t(k).replace("{city}", city.name);

  const schemas = [
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "All Rajasthan", path: "/all-rajasthan-taxi" },
      { name: city.name, path: `/taxi-service-${slug}` },
    ]),
    serviceSchema(title, city.metaDesc, `/taxi-service-${slug}`),
    faqSchema(city.faqs),
  ];

  return (
    <>
      <SEO title={city.metaTitle.replace(" — Blue City Taxi", "")} description={city.metaDesc} path={`/taxi-service-${slug}`} image={city.img} schemas={schemas} />
      <PageHero
        title={title}
        subtitle={`${city.tag} — ${t("cHeroSub")}`}
        image={city.img}
        crumbs={[{ label: "Home", to: "/" }, { label: "All Rajasthan", to: "/all-rajasthan-taxi" }, { label: city.name }]}
      />

      <section className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <Reveal>
            <p className="text-slate-700 leading-relaxed text-sm sm:text-base">{intro}</p>
          </Reveal>

          <Reveal>
            <div className="grid sm:grid-cols-2 gap-4">
              {blocks.map(([h, d]) => (
                <div key={h} className="bg-white rounded-2xl border border-slate-200/80 p-5">
                  <h2 className="font-heading font-bold text-navy flex items-center gap-2"><Check size={16} className="text-gold-dark" /> {h}</h2>
                  <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-heading text-2xl font-extrabold text-navy">{tc("cAttr")}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {city.attractions.map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 rounded-full bg-royal-50 text-royal-700 text-xs font-semibold px-3.5 py-2">
                  <MapPin size={12} /> {a}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-heading text-2xl font-extrabold text-navy">{t("cRoutes")}</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {city.routes.map((r) => {
                const [from, ...rest] = r.split("-");
                const toSlug = rest.join("-");
                const toCity = CITIES.find((c) => c.slug === toSlug);
                const label = `${cap(from)} to ${cap(toSlug)} Taxi`;
                return (
                  <Link
                    key={r}
                    to={toCity && toCity.slug !== slug ? `/taxi-service-${toSlug}` : "/one-way-taxi"}
                    data-testid={`route-link-${r}`}
                    className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-semibold text-navy hover:border-royal hover:text-royal transition-colors"
                  >
                    {label} <ArrowRight size={15} className="text-gold-dark shrink-0" />
                  </Link>
                );
              })}
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-heading text-2xl font-extrabold text-navy">{tc("cWhy")}</h2>
            <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
              {why.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-slate-700 bg-white rounded-xl border border-slate-200/80 px-4 py-3">
                  <Check size={15} className="text-gold-dark mt-0.5 shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal>
            <h2 className="font-heading text-2xl font-extrabold text-navy">{t("cFaq")} — {title}</h2>
            <div className="mt-4 bg-white rounded-2xl border border-slate-200/80 px-6">
              <FaqAccordion faqs={faqs} idPrefix={`city-${slug}`} />
            </div>
          </Reveal>
        </div>

        <div className="lg:sticky lg:top-28 h-fit">
          <BookingWidget compact title={tc("cBook")} />
          <Reveal delay={0.1} className="mt-5">
            <div className="rounded-2xl bg-navy p-5 text-center grain relative overflow-hidden">
              <div className="gold-line absolute top-0 left-0 right-0" />
              <p className="text-xs font-bold uppercase tracking-widest text-gold">{t("cOther")}</p>
              <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                {CITIES.filter((c) => c.slug !== slug).slice(0, 8).map((c) => (
                  <Link key={c.slug} to={`/taxi-service-${c.slug}`} data-testid={`other-city-${c.slug}`} className="text-[11px] font-semibold text-white/70 hover:text-gold border border-white/15 rounded-full px-2.5 py-1 transition-colors">
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand title={tc("cCtaTitle")} text={t("cCtaText").replace("{city}", city.name)} />
    </>
  );
}
